<template>
  <div
    v-if="isProfileFlyoutRendered"
    ref="flyoutRef"
    class="profile-flyout-fi"
  >
    <div
      class="profile-flyout-fi__background"
      :class="{ 'profile-flyout-fi__background--visible': isVisible }"
      @click="showProfileFlyout = false"
    />

    <div
      class="profile-flyout-fi__content"
      :class="{ 'profile-flyout-fi__content--visible': isVisible }"
    >
      <div class="profile-flyout-fi__close">
        <icon-fi
          icon="close"
          @click="showProfileFlyout = false"
        />
      </div>

      <template v-if="!isLoading && sitecoreData">
        <div class="profile-flyout-fi__personal-data">
          <profile-flyout-personal-data-fi
            :customer-profile="customerProfile || {}"
            :sitecore-data="sitecoreData"
          />
        </div>

        <div class="profile-flyout-fi__link-list">
          <profile-flyout-link-list-fi
            :sitecore-data="sitecoreData"
            :customer-documents="customerDocuments"
          />
        </div>

        <div
          v-if="
            (fischer.shop.isClosedShop() || fischer.shop.isOnlineShop()) &&
            sitecoreData.ActivateMyfischerCopilot
          "
          class="profile-flyout-fi__chat-box"
        >
          <profile-flyout-chat-box-fi :sitecore-data="sitecoreData" />
        </div>

        <div class="profile-flyout-fi__contact">
          <profile-flyout-contact-fi :sitecore-data="sitecoreData" />
        </div>

        <!--  Hide it until it's clear where the offers are coming from
        <div class="profile-flyout-fi__offers">
          <profile-flyout-offers-fi :sitecore-data="sitecoreData" />
        </div>
        -->
      </template>

      <loading-indicator
        v-else
        variant="spinnerLocal"
        class="profile-flyout-fi__loader"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import { LoadingIndicator } from 'atoms';
import { IconFi } from 'atoms';
import { useNotificationStore } from 'store/notification';
import { useProfileFlyoutStore } from 'store/profile-flyout';
import { computed, nextTick, ref, toRefs, watch } from 'vue';

import { useAssetUrl } from '@/composables/assetUrl';
import { useBodyScroll } from '@/composables/bodyScroll';
import { useFischer } from '@/composables/fischerPlugin';
import { useUser } from '@/composables/user';
import type {
  DocumentDownload,
  GetCustomerDocumentsQuery,
  GetCustomerDocumentsQueryVariables,
} from '@/generated/graphql';
import GETCUSTOMERDOCUMENTS from '@/graphql/getCustomerDocuments.gql';
import GETCUSTOMERPROFILE from '@/graphql/getCustomerProfile.gql';

import ProfileFlyoutChatBoxFi from './profile-flyout-chat-box-fi/ProfileFlyoutChatBoxFi.vue';
import ProfileFlyoutContactFi from './profile-flyout-contact-fi/ProfileFlyoutContactFi.vue';
import ProfileFlyoutLinkListFi from './profile-flyout-link-list-fi/ProfileFlyoutLinkListFi.vue';
//import ProfileFlyoutOffersFi from './profile-flyout-offers-fi/ProfileFlyoutOffersFi.vue';
import ProfileFlyoutPersonalDataFi from './profile-flyout-personal-data-fi/ProfileFlyoutPersonalDataFi.vue';
import type { ProfileFlyoutData } from './profile-flyout-types';

defineOptions({ name: 'ProfileFlyoutFi' });

const fischer = useFischer();
const { logoutIfTokenInvalid } = useUser();
const handle = useClientHandle();
const { bodyDisableScroll, bodyEnableScroll } = useBodyScroll();
const { showProfileFlyout, isProfileFlyoutRendered } = toRefs(useProfileFlyoutStore());
const { openGeneralErrorNotification } = useNotificationStore();
const { getAssetUrl } = useAssetUrl();

const getCustomerProfileLoading = ref(false);
const getCustomerDocumentsLoading = ref(false);
const getSitecoreDataLoading = ref(false);
const isVisible = ref(false);
const flyoutRef = ref<HTMLElement | null>(null);
const sitecoreData = ref<ProfileFlyoutData | null>(null);
const customerProfile = ref<Record<string, any> | null>(null);
const customerDocuments = ref<DocumentDownload[] | null>(null);

const isLoading = computed(
  () =>
    getCustomerProfileLoading.value ||
    getCustomerDocumentsLoading.value ||
    getSitecoreDataLoading.value,
);

const assistedService = computed(() => fischer.user.getAssistedService());
const sapCustomerId = computed(() => assistedService.value?.user?.customerId);

const getSitecoreData = async () => {
  if (sitecoreData.value) {
    return;
  }

  const api = IS_SHOWROOM
    ? getAssetUrl('/__mocks__/data/profileFlyout.json')
    : `${fischer.hostUrlBase()}/myfischer/api/app?appType=profileFlyout&lang=${fischer.siteLanguage()}`;
  try {
    getSitecoreDataLoading.value = true;
    const response = await fetch(api);
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    const data = await response.json();
    sitecoreData.value = data;
  } catch (error) {
    console.error('could not load sitecore data', error);
    showProfileFlyout.value = false;
    openGeneralErrorNotification();
  } finally {
    getSitecoreDataLoading.value = false;
  }
};

const getCustomerProfile = async () => {
  if (customerProfile.value) {
    return;
  }

  try {
    getCustomerProfileLoading.value = true;
    const { data, error } = await handle.client
      .query(GETCUSTOMERPROFILE, {
        baseSiteId: fischer.baseSiteId(),
        accessToken: fischer.user.accessToken(),
        userId: fischer.user.id(),
        division: fischer.user.division(),
        distributionChannel: fischer.user.distributionChannel(),
      })
      .toPromise();

    // urql resolves with { data, error } rather than throwing, so the invalid-token
    // logout has to be driven off `error` explicitly.
    if (error) {
      showProfileFlyout.value = false;
      logoutIfTokenInvalid(error);
      return;
    }

    customerProfile.value = data?.getCustomerProfile;
  } catch (err) {
    showProfileFlyout.value = false;
    logoutIfTokenInvalid(err);
  } finally {
    getCustomerProfileLoading.value = false;
  }
};

const getCustomerDocuments = async () => {
  const documentsWidget = sitecoreData.value?.Widgets.find(
    (widget) => widget.Identifier === 'Documents',
  );

  const hasDocumentsAccess =
    !!documentsWidget &&
    (!documentsWidget.RequiredGroups?.length ||
      documentsWidget.RequiredGroups.every((group) => fischer.user.groups()?.includes(group)));

  if (customerDocuments.value || !hasDocumentsAccess) {
    return;
  }

  try {
    getCustomerDocumentsLoading.value = true;
    const { data, error } = await handle.client
      .query<GetCustomerDocumentsQuery, GetCustomerDocumentsQueryVariables>(
        GETCUSTOMERDOCUMENTS,
        {
          jwtToken: fischer.user.idToken(),
          sapCustomerId: sapCustomerId.value,
          baseSiteId: fischer.baseSiteId(),
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise();

    if (error) {
      console.debug('Did not receive any documents');
      return;
    }

    customerDocuments.value =
      data?.getCustomerDocuments?.filter((document): document is DocumentDownload => !!document) ??
      null;
  } catch (err) {
    console.error(err);
  } finally {
    getCustomerDocumentsLoading.value = false;
  }
};

watch(
  () => showProfileFlyout.value,
  async (newValue) => {
    if (newValue) {
      getSitecoreData().then(getCustomerDocuments);
      getCustomerProfile();
      isProfileFlyoutRendered.value = true;
      bodyDisableScroll();

      await nextTick();

      // Firefox does not necessarily compute the initial (off-screen) styles of the
      // freshly inserted element before the visibility class is applied, so the
      // transition has no starting point and the flyout pops in instantly. Reading
      // a layout property forces a style/layout flush and makes the start state real.
      void flyoutRef.value?.offsetHeight;

      isVisible.value = true;
    } else {
      isVisible.value = false;
      setTimeout(() => {
        isProfileFlyoutRendered.value = false;
        bodyEnableScroll();
      }, 500);
    }
  },
);
</script>

<style scoped src="./profile-flyout-fi" />
